import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { connectAdminSocket } from "../services/adminSocket";

// Only super admins monitor incoming ride requests.
const isSuperAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").role === "super_admin";
  } catch {
    return false;
  }
};

// Short WebAudio chime — no asset needed, plays after any user gesture.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1180, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
  } catch {
    /* audio is best-effort */
  }
}

/**
 * Global, headless listener: keeps a realtime socket open for the logged-in
 * super admin and surfaces every new ride request as an in-app toast, a desktop
 * notification, and a chime. Mounted once at the app root.
 */
function AdminRideAlerts() {
  const navigate = useNavigate();
  const location = useLocation();
  const boundRef = useRef(false);

  useEffect(() => {
    if (!isSuperAdmin()) return;

    const socket = connectAdminSocket();
    if (!socket) return;

    // Ask for desktop-notification permission once.
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    // Bind handlers only once even though the effect re-runs on navigation.
    if (boundRef.current) return;
    boundRef.current = true;

    const onRideRequested = (data) => {
      const route =
        data?.pickupLocationName && data?.dropoffLocationName
          ? `${data.pickupLocationName} → ${data.dropoffLocationName}`
          : "New ride request";
      const who = data?.passengerName || "A passenger";
      const notified =
        typeof data?.driversNotified === "number"
          ? ` · ${data.driversNotified} rider${data.driversNotified === 1 ? "" : "s"} notified`
          : "";

      playChime();

      toast(
        (t) => (
          <span
            onClick={() => {
              toast.dismiss(t.id);
              navigate("/trips");
            }}
            style={{ cursor: "pointer", display: "block" }}
          >
            <strong>🚗 New ride request</strong>
            <br />
            {who}: {route}
            {notified}
          </span>
        ),
        { duration: 8000, icon: "🔔" },
      );

      if ("Notification" in window && Notification.permission === "granted") {
        try {
          const n = new Notification("🚗 New ride request", {
            body: `${who}: ${route}${notified}`,
            tag: data?.bookingId || undefined,
            renotify: true,
          });
          n.onclick = () => {
            window.focus();
            navigate("/trips");
            n.close();
          };
        } catch {
          /* notification best-effort */
        }
      }
    };

    socket.on("admin:ride_requested", onRideRequested);

    return () => {
      socket.off("admin:ride_requested", onRideRequested);
      boundRef.current = false;
    };
    // Re-evaluate on route change so it connects right after login navigates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}

export default AdminRideAlerts;
